import { Box, Button, Tab, Tabs } from "@mui/material";
import { type MRT_ColumnDef } from "material-react-table";
import * as React from "react";
import toast from "react-hot-toast";
import { useLocation, useNavigate } from "react-router-dom";
import { useDeleteTagMutation, useEditTagMutation, useGetTagsQuery } from "../../api/apiSlice";
import { ConfirmDialog, CustomTabPanel, Table } from "../../components";
import { Drawer } from "../../components/Drawer/Drawer";
import { useHasPermission } from "../../hooks";
import { appendSearchParams, getApiErrorMessage } from "../../utils";
import { PageTabParams } from "../../utils/constants";
import { UserPermissions } from "../../utils/permissions";
import { ITag, ModalEnum, ModalType, PreviewTag, TabKeyEnum, TabLabelEnum } from "../../utils/types";
import { CreateOrEditTag } from "./CreateOrEditTag";
import { useGetTagsTableColumns } from "./useGetTagsTableColumns";
import { ViewTag } from "./ViewTag";

export const Tags = () => {
  const [pagination, setPagination] = React.useState({
    pageIndex: 0,
    pageSize: 20,
  });

  const location = useLocation();
  const navigate = useNavigate();

  const [urlParams] = React.useState(new URLSearchParams(location.search));
  const [selectedTag, setSelectedTag] = React.useState<PreviewTag | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = React.useState<ModalType | undefined>();
  const [hoveredRow, setHoveredRow] = React.useState(-1);
  const [isUnpublishDialogOpen, setIsUnpublishDialogOpen] = React.useState(false);
  const [isRemoveDialogOpen, setIsRemoveDialogOpen] = React.useState(false);
  const [isPublishDialogOpen, setIsPublishDialogOpen] = React.useState(false);
  const [isReviewDialogOpen, setIsReviewDialogOpen] = React.useState(false);
  const [filter, setFilter] = React.useState<string | undefined>(undefined);
  const [sorting, setSorting] = React.useState([{ id: "updatedAt", desc: true }]);

  const [editTag, editTagResult] = useEditTagMutation();
  const [deleteTag, deleteTagResult] = useDeleteTagMutation();

  const canViewUnpublishedTags = useHasPermission(UserPermissions.VIEW_UNPUBLISHED_TAGS);
  const canViewReadyToReviewTags = useHasPermission(UserPermissions.VIEW_READY_TO_REVIEW_TAGS);

  const visibleTabs = PageTabParams.filter(({ value }) => {
    if (value === TabKeyEnum.Archived) return false;
    if (value === TabKeyEnum.Unpublished && !canViewUnpublishedTags) return false;
    if (value === TabKeyEnum.ReadyForReview && !canViewReadyToReviewTags) return false;
    return true;
  });

  const rawTabParam = (urlParams.get("activeTab") ?? visibleTabs[0]?.value ?? PageTabParams[0].value) as TabKeyEnum;
  const activeTabParam = visibleTabs.some(({ value }) => value === rawTabParam) ? rawTabParam : visibleTabs[0].value;
  const activeTabValue = Math.max(
    0,
    visibleTabs.findIndex(({ value }) => value === activeTabParam),
  );

  const apiSupportedParams = visibleTabs[activeTabValue].params;
  const { data, isLoading, isFetching } = useGetTagsQuery({
    ...apiSupportedParams,
    tag: filter || undefined,
    page: pagination.pageIndex + 1,
    per_page: pagination.pageSize,
    expand: "createdBy,updatedBy",
    sortBy: (sorting[0] ?? { id: "updatedAt", desc: true }).id,
    sortOrder: (sorting[0] ?? { id: "updatedAt", desc: true }).desc ? "DESC" : "ASC",
  });

  const tagsState = React.useMemo(() => data?.data || [], [data]);
  const tagsData = React.useMemo<MRT_ColumnDef<PreviewTag>[]>(
    () =>
      tagsState?.map((item: ITag) => ({
        ...item,
        totalQuotes: item.totalQuotes ?? 0,
        totalUsers: item.totalSubscribers ?? 0,
        createdBy: item.createdBy?.name,
        updatedBy: item.updatedBy?.name,
        published:
          item.published === 0 ? TabLabelEnum.Unpublished : item.published === -1 ? TabLabelEnum.ReadyForReview : TabLabelEnum.Published,
      })),
    [tagsState],
  );

  const total = data?.total || 0;

  const handleChange = (_event: React.SyntheticEvent, newValue: number) => {
    const activeTab = visibleTabs[newValue].value;
    const newUrlParams = appendSearchParams({ activeTab }, urlParams);
    navigate(`${location.pathname}?${newUrlParams.toString()}`, {
      replace: true,
    });
  };

  const getDrawerTitle = () => {
    if (isDrawerOpen === ModalEnum.Preview) {
      return "Preview Tag";
    }
    if (isDrawerOpen === ModalEnum.Create) {
      return "Create Tag";
    }
    if (isDrawerOpen === ModalEnum.Edit) {
      return "Edit Tag";
    }
    return "";
  };

  const handlePreviewTag = () => {
    setIsDrawerOpen(ModalEnum.Preview);
  };

  const handleCreateTag = () => {
    setHoveredRow(-1);
    setSelectedTag(null);
    setIsDrawerOpen(ModalEnum.Create);
  };

  const handleEditTag = () => {
    setIsDrawerOpen(ModalEnum.Edit);
  };

  const handleOpenRemoveConfirmDialog = () => {
    setIsRemoveDialogOpen(true);
  };
  const handleRemoveTag = () => {
    if (selectedTag?.id) {
      deleteTag({ id: selectedTag.id })
        .unwrap()
        .then(() => {
          setIsRemoveDialogOpen(false);
          toast.success("Tag removed successfully");
        })
        .catch(error => {
          const errorMessage = getApiErrorMessage(error, "Failed to remove tag");
          toast.error(errorMessage);
        });
    } else {
      console.error("No tag selected");
    }
  };

  const handleToggleUnpublishConfirmDialog = (open: boolean) => {
    setIsUnpublishDialogOpen(open);
  };

  const handleTogglePublishConfrimDialog = (open: boolean) => {
    setIsPublishDialogOpen(open);
  };

  const handleToggleReviewConfrimDialog = (open: boolean) => {
    setIsReviewDialogOpen(open);
  };

  const handlePublishChange = (published: TabKeyEnum) => {
    const messages = {
      [TabKeyEnum.Published]: {
        value: 1,
        success: "Tag published successfully",
        error: "Failed to publish tag",
      },
      [TabKeyEnum.ReadyForReview]: {
        value: -1,
        success: "Tag saved successfully",
        error: "Failed to saved tag",
      },
      [TabKeyEnum.Unpublished]: {
        value: 0,
        success: "Tag unpublish successfully",
        error: "Failed to unpublish tag",
      },
      [TabKeyEnum.Archived]: {
        value: 0,
        success: "Tag archived successfully",
        error: "Failed to archive tag",
      },
    };

    if (selectedTag?.id) {
      editTag({
        id: selectedTag?.id,
        published: messages[published].value,
        tag: selectedTag?.tag,
        type: selectedTag?.type,
      })
        .unwrap()
        .then(() => {
          handleToggleUnpublishConfirmDialog(false);
          handleTogglePublishConfrimDialog(false);
          handleToggleReviewConfrimDialog(false);
          handleDrawerClose();
          toast.success(messages[published].success);
        })
        .catch(error => {
          const errorMessage = getApiErrorMessage(error, messages[published].error);
          toast.error(errorMessage);
        });
    } else {
      console.error("No tag selected");
    }
  };

  const handleDrawerClose = () => {
    setIsDrawerOpen(undefined);
  };

  const columns = useGetTagsTableColumns({
    activeTab: activeTabParam,
    handlePreviewTag,
    handleEditTag,
    setHoveredRow,
    hoveredRow,
    handleRemoveTag: handleOpenRemoveConfirmDialog,
    handleUnpublishTag: () => handleToggleUnpublishConfirmDialog(true),
    handlePublishTag: () => handleTogglePublishConfrimDialog(true),
  });

  const canCreateTag = useHasPermission(UserPermissions.CREATE_TAGS);

  return (
    <Box>
      <Drawer title={getDrawerTitle()} onClose={handleDrawerClose} open={!!isDrawerOpen} sx={{ width: "500px", height: "100%" }}>
        {isDrawerOpen === ModalEnum.Preview && (
          <ViewTag
            activeTab={activeTabParam}
            handleClose={handleDrawerClose}
            handleEditTag={handleEditTag}
            tag={selectedTag}
            handlePublishTag={() => handleTogglePublishConfrimDialog(true)}
            handleUnpublishTag={() => handleToggleUnpublishConfirmDialog(true)}
            handleReviewTag={() => handleToggleReviewConfrimDialog(true)}
          />
        )}
        {(isDrawerOpen === ModalEnum.Create || isDrawerOpen === ModalEnum.Edit) && (
          <CreateOrEditTag
            activeTab={activeTabParam}
            handleClose={handleDrawerClose}
            tag={selectedTag}
            isEditing={isDrawerOpen === ModalEnum.Edit}
          />
        )}
      </Drawer>

      <ConfirmDialog
        open={isUnpublishDialogOpen}
        title={`Unpublish Tag: ${selectedTag?.tag}`}
        body={`Are you sure you want to unpublish this tag?`}
        onClose={() => handleToggleUnpublishConfirmDialog(false)}
        onConfirm={() => handlePublishChange(TabKeyEnum.Unpublished)}
        loading={editTagResult.isLoading}
      />

      <ConfirmDialog
        open={isRemoveDialogOpen}
        title={`Remove Tag: ${selectedTag?.tag}`}
        body={`Are you sure you want to remove this tag? This action is permanent and cannot be undone.`}
        onClose={() => setIsRemoveDialogOpen(false)}
        onConfirm={handleRemoveTag}
        loading={deleteTagResult.isLoading}
      />

      <ConfirmDialog
        open={isPublishDialogOpen}
        title={`Publish Tag: ${selectedTag?.tag}`}
        body={`Are you sure you want to publish this tag?`}
        onClose={() => handleTogglePublishConfrimDialog(false)}
        onConfirm={() => handlePublishChange(TabKeyEnum.Published)}
        loading={editTagResult.isLoading}
      />

      <ConfirmDialog
        open={isReviewDialogOpen}
        title={`Save Tag: ${selectedTag?.tag}`}
        body={`Are you sure you want to save this tag?`}
        onClose={() => handleToggleReviewConfrimDialog(false)}
        onConfirm={() => handlePublishChange(TabKeyEnum.ReadyForReview)}
        loading={editTagResult.isLoading}
      />
      <Box display="flex" justifyContent="space-between">
        <Tabs value={activeTabValue} onChange={handleChange}>
          {visibleTabs.map((value, index: number) => {
            return (
              <Tab
                key={value.label}
                label={value.label}
                sx={{
                  p: 2,
                  mr: 2,
                }}
              />
            );
          })}
        </Tabs>
        {canCreateTag && (
          <Box>
            <Button onClick={handleCreateTag} sx={{ borderRadius: 0.5 }} color="primary" variant="outlined">
              CREATE TAG
            </Button>
          </Box>
        )}
      </Box>

      <Box sx={{ width: "100%" }}>
        <CustomTabPanel value={activeTabValue} index={activeTabValue}>
          <Table
            columns={columns}
            data={tagsData}
            manualPagination
            manualFiltering
            onGlobalFilterChange={setFilter}
            onPaginationChange={setPagination}
            manualSorting
            enableMultiSort={false}
            onSortingChange={setSorting}
            state={{ pagination, sorting }}
            rowCount={total}
            loading={isLoading || isFetching}
            muiTableBodyRowProps={({ row }: { row: { id: string; index: number; original: PreviewTag } }) => ({
              onMouseEnter: () => {
                setHoveredRow(row.index);
                setSelectedTag(row.original);
              },
              onClick: () => {
                handlePreviewTag();
              },
            })}
          />
        </CustomTabPanel>
      </Box>
    </Box>
  );
};
